/**
 * Top bar: source identity, engine truth, panel access.
 *
 * The right-hand cluster is deliberately blunt about what the browser is
 * actually doing (spec 33, 34): real-time or offline, the context rate, whether
 * the OS is resampling, and the measured latency. No "bit perfect" claim is made
 * anywhere, because a web page cannot establish that.
 */

import { useRef } from 'react';
import { engine } from '../../audio/engine';
import { useApp } from '../../stores/appStore';
import { Spinner, fmtBytes } from './primitives';

export function TopBar({ onOpenPanel }: { onOpenPanel: (p: string) => void }) {
  const metadata = useApp((s) => s.metadata);
  const analysis = useApp((s) => s.analysis);
  const analysing = useApp((s) => s.analysing);
  const analysisNote = useApp((s) => s.analysisNote);
  const loadingFile = useApp((s) => s.loadingFile);
  const loadFile = useApp((s) => s.loadFile);
  const logs = useApp((s) => s.logs);
  const engineReady = useApp((s) => s.engineReady);

  const inputRef = useRef<HTMLInputElement>(null);
  const caps = engine.capabilities;
  const errors = logs.filter((l) => l.level === 'error').length;
  const warnings = logs.filter((l) => l.level === 'warn').length;

  const rateNote = (() => {
    if (!engineReady) return 'not started';
    const ctxRate = caps.contextSampleRate;
    const src = metadata?.sampleRate;
    if (src && src !== ctxRate) {
      return `source ${src / 1000} kHz resampled to ${ctxRate / 1000} kHz by the browser`;
    }
    if (src) return `native — no resampling in the graph`;
    return `${ctxRate / 1000} kHz`;
  })();

  return (
    <div className="topbar">
      <div className="brand">
        DAC<em>·</em>ENGINE <span>hardware simulation laboratory</span>
      </div>

      <div style={{ width: 1, height: 26, background: 'var(--line)' }} />

      <div className="file-strip">
        <input
          ref={inputRef}
          type="file"
          accept=".wav,.flac,.aiff,.aif,.mp3,.m4a,.ogg,.opus,audio/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void loadFile(f).catch(() => undefined);
            e.target.value = '';
          }}
        />
        <button className="btn sm" onClick={() => inputRef.current?.click()} disabled={loadingFile}>
          {loadingFile ? <Spinner /> : '▤'} Open file
        </button>

        {metadata ? (
          <>
            <span className="file-name" title={metadata.fileName}>
              {metadata.fileName}
            </span>
            <div className="kv">
              <b>{(metadata.sampleRate / 1000).toFixed(1)} kHz</b>
              <i>rate</i>
            </div>
            <div className="kv">
              <b>{metadata.bitDepth ? `${metadata.bitDepth} bit` : '--'}</b>
              <i>depth</i>
            </div>
            <div className="kv">
              <b>{metadata.channels}</b>
              <i>ch</i>
            </div>
            <div className="kv">
              <b>{metadata.codec}</b>
              <i>codec</i>
            </div>
            <div className="kv">
              <b>{fmtBytes(metadata.fileSize)}</b>
              <i>size</i>
            </div>
            {analysing ? (
              <div className="kv">
                <b>
                  <Spinner />
                </b>
                <i>{analysisNote ?? 'analysing'}</i>
              </div>
            ) : analysis ? (
              <>
                <div className="kv">
                  <b>{analysis.peakDbFS.toFixed(1)}</b>
                  <i>peak dBFS</i>
                </div>
                <div className="kv">
                  <b>{analysis.lufsIntegrated.toFixed(1)}</b>
                  <i>LUFS-I</i>
                </div>
                <div className="kv">
                  <b>{analysis.crestFactorDb.toFixed(1)}</b>
                  <i>crest dB</i>
                </div>
                <div className="kv">
                  <b>{analysis.dynamicRangeDb.toFixed(1)}</b>
                  <i>PLR dB</i>
                </div>
              </>
            ) : null}
          </>
        ) : (
          <span className="dim small">Drop a FLAC or WAV anywhere, or press Open file.</span>
        )}
      </div>

      <div className="row" style={{ gap: 5 }}>
        <span className="pill rt" title={`Real-time playback. Context ${caps.contextSampleRate} Hz, ${rateNote}.`}>
          REAL-TIME
        </span>
        <span className="mono tiny faint" title={rateNote} style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {rateNote}
        </span>
        <button className="btn sm" onClick={() => onOpenPanel('engine')} title="Engine, quality and performance">
          Engine
        </button>
        <button className="btn sm" onClick={() => onOpenPanel('rawdsp')} title="Raw DSP state">
          DSP
        </button>
        <button className="btn sm" onClick={() => onOpenPanel('convert')} title="Offline render and export">
          Render
        </button>
        <button className="btn sm" onClick={() => onOpenPanel('measure')} title="Measurement Lab">
          Measure
        </button>
        <button className="btn sm" onClick={() => onOpenPanel('test')} title="Test signal generator">
          Signals
        </button>
        <button className="btn sm" onClick={() => onOpenPanel('diagnostics')} title="Audio engine diagnostics">
          Diag
        </button>
        <button
          className={`btn sm${errors ? ' danger' : ''}`}
          onClick={() => onOpenPanel('console')}
          title="Debug console"
        >
          Console{errors > 0 ? ` (${errors})` : warnings > 0 ? ` (${warnings}!)` : ''}
        </button>
      </div>
    </div>
  );
}
